import { Link } from "react-router-dom";
import { BookOpen, Download, Flame, ShoppingBag, ArrowRight, UtensilsCrossed, CalendarCheck } from "lucide-react";
import { Reveal, Overline } from "@/components/site/Reveal";
import { EBOOK, INTERAC_EMAIL, EBOOK_BUY_MSG, waLink } from "@/lib/data";
import { useLang } from "@/lib/i18n";

const Ebook = () => {
    const { tr } = useLang();

    const CHAPTERS = [
        [tr("Partie 1", "Part 1"), tr("Les 10 piliers d'une assiette qui brûle le gras + ta cible calorique", "The 10 pillars of a fat-burning plate + your calorie target")],
        [tr("Partie 2", "Part 2"), tr("45 recettes recalibrées : mafé, thiéboudienne, ndolé, kedjenou, attiéké…", "45 recalibrated recipes: mafé, thiéboudienne, ndolé, kedjenou, attiéké…")],
        [tr("Partie 3", "Part 3"), tr("8 semaines de plans de repas complets, jour par jour", "8 weeks of complete meal plans, day by day")],
        [tr("Partie 4", "Part 4"), tr("Listes d'épicerie par quinzaine, prêtes à imprimer", "Bi-weekly grocery lists, ready to print")],
        ["Bonus", tr("12 sauces et marinades allégées qui gardent tout le caractère", "12 light sauces and marinades that keep all the character")],
    ];

    return (
        <div data-testid="ebook-page" className="pt-28 md:pt-36">
            <section className="pb-24 md:pb-32">
                <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
                    <Reveal className="lg:col-span-5 lg:sticky lg:top-32">
                        <div className="relative">
                            <div className="absolute -inset-4 border border-ochre/25 -translate-x-5 translate-y-5" />
                            <div className="relative overflow-hidden bg-obsidian-surface">
                                <img
                                    src={EBOOK.cover}
                                    alt={tr(EBOOK.title)}
                                    className="w-full aspect-[4/5] object-cover"
                                    data-testid="ebook-cover-image"
                                />
                                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
                                <div className="absolute bottom-8 left-8 right-8">
                                    <p className="text-xs uppercase tracking-[0.25em] text-ochre">Ebook — PDF</p>
                                    <p className="mt-2 font-heading text-3xl md:text-4xl font-black uppercase tracking-tight leading-tight">
                                        {tr(EBOOK.title)}
                                    </p>
                                    <p className="mt-2 font-accent italic text-ochre">{tr("par Coach Roméo", "by Coach Roméo")}</p>
                                </div>
                            </div>
                        </div>
                    </Reveal>

                    <div className="lg:col-span-7">
                        <Reveal>
                            <Overline>{tr("L'Ebook signature", "The signature Ebook")}</Overline>
                            <h1 className="mt-4 font-heading text-4xl sm:text-5xl font-bold tracking-tight leading-tight">
                                {tr(EBOOK.subtitle)}.
                            </h1>
                            <p className="mt-6 text-sand leading-relaxed font-light">
                                {tr(
                                    "Pendant des années, on t'a dit que perdre du gras voulait dire abandonner ta culture. Cet ebook prouve le contraire, recette après recette. Tu vas apprendre à transformer les plats qui t'ont vu grandir en une machine à brûler le gras — sans frustration, sans salade triste, sans renoncer aux grandes tablées.",
                                    "For years, you were told that losing fat meant giving up your culture. This ebook proves the opposite, recipe after recipe. You'll learn to turn the dishes you grew up with into a fat-burning machine — no frustration, no sad salads, no skipping family dinners."
                                )}
                            </p>
                        </Reveal>

                        <Reveal delay={0.1} className="mt-10">
                            <ul className="space-y-4">
                                {EBOOK.bullets.map((b) => (
                                    <li key={b.fr} className="flex items-start gap-3 text-cream/85">
                                        <Flame className="h-4 w-4 mt-1 text-terracotta shrink-0" />
                                        {tr(b)}
                                    </li>
                                ))}
                            </ul>
                        </Reveal>

                        <Reveal delay={0.15} className="mt-10">
                            <div className="border border-white/10 bg-obsidian-surface p-8">
                                <p className="text-xs uppercase tracking-[0.25em] text-sand">{tr("Sommaire", "Table of contents")}</p>
                                <div className="mt-5 divide-y divide-white/10">
                                    {CHAPTERS.map(([num, title]) => (
                                        <div key={num} className="py-4 flex items-baseline gap-4">
                                            <span className="font-accent italic text-terracotta text-sm shrink-0 w-20">{num}</span>
                                            <span className="text-cream/85 font-light">{title}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </Reveal>

                        <Reveal delay={0.2} className="mt-10">
                            <div className="border border-terracotta/50 bg-terracotta/5 p-8">
                                <div className="flex flex-col sm:flex-row sm:items-center gap-6">
                                    <div className="flex-1">
                                        <p className="font-heading text-4xl font-black tracking-tighter">{EBOOK.price}</p>
                                        <p className="mt-1 text-sm text-sand">
                                            {tr("Paiement par virement Interac uniquement.", "Payment by Interac e-transfer only.")}
                                        </p>
                                    </div>
                                    <a
                                        href={waLink(tr(EBOOK_BUY_MSG))}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        data-testid="ebook-buy-button"
                                        className="shrink-0 inline-flex items-center justify-center gap-3 rounded-full bg-terracotta px-8 py-4 text-sm font-bold uppercase tracking-wider text-white transition-colors duration-300 hover:bg-terracotta-hover"
                                    >
                                        <ShoppingBag className="h-4 w-4" /> {tr("Acheter l'Ebook", "Buy the Ebook")}
                                    </a>
                                </div>
                                <ol className="mt-6 space-y-3 text-sm text-cream/85 border-t border-white/10 pt-6" data-testid="ebook-payment-steps">
                                    <li className="flex gap-3">
                                        <span className="font-accent italic font-bold text-terracotta">1.</span>
                                        {tr("Envoie", "Send")} {EBOOK.price} {tr("par virement Interac à", "by Interac e-transfer to")}{" "}
                                        <span className="font-bold text-ochre">{INTERAC_EMAIL}</span>
                                    </li>
                                    <li className="flex gap-3">
                                        <span className="font-accent italic font-bold text-terracotta">2.</span>
                                        {tr(
                                            "Confirme ton paiement sur WhatsApp (capture d'écran ou nom du virement)",
                                            "Confirm your payment on WhatsApp (screenshot or transfer name)"
                                        )}
                                    </li>
                                    <li className="flex gap-3">
                                        <span className="font-accent italic font-bold text-terracotta">3.</span>
                                        {tr("Reçois ton Ebook PDF par courriel sous 24 h", "Receive your PDF Ebook by email within 24 hours")}
                                    </li>
                                </ol>
                            </div>
                            <div className="mt-6 flex flex-wrap gap-x-8 gap-y-3 text-sm text-sand">
                                <span className="flex items-center gap-2"><Download className="h-4 w-4 text-ochre" /> {tr("PDF — lecture immédiate", "PDF — instant reading")}</span>
                                <span className="flex items-center gap-2"><BookOpen className="h-4 w-4 text-ochre" /> {tr("35 pages, 6 plats illustrés", "35 pages, 6 illustrated dishes")}</span>
                                <span className="flex items-center gap-2"><UtensilsCrossed className="h-4 w-4 text-ochre" /> {tr("Recettes testées et approuvées", "Tested and approved recipes")}</span>
                            </div>
                        </Reveal>

                        <Reveal delay={0.25} className="mt-12">
                            <div className="border border-ochre/30 bg-obsidian-surface p-8 flex flex-col md:flex-row md:items-center justify-between gap-6">
                                <div>
                                    <h3 className="font-heading text-xl font-bold tracking-tight flex items-center gap-2">
                                        <CalendarCheck className="h-5 w-5 text-ochre" /> {tr("Envie d'un suivi personnalisé ?", "Want personal coaching?")}
                                    </h3>
                                    <p className="mt-2 text-sm text-sand font-light">
                                        {tr(
                                            "Réserve ta consultation de 30 minutes (25 $) sur Zoom pour aller plus loin que l'Ebook.",
                                            "Book your 30-minute consultation ($25) on Zoom to go further than the Ebook."
                                        )}
                                    </p>
                                </div>
                                <Link
                                    to="/contact"
                                    data-testid="ebook-cta-consultation"
                                    className="shrink-0 inline-flex items-center justify-center gap-3 rounded-full bg-ochre px-6 py-3.5 text-sm font-bold uppercase tracking-wider text-obsidian transition-colors duration-300 hover:bg-ochre-hover"
                                >
                                    {tr("Réserver", "Book")} <ArrowRight className="h-4 w-4" />
                                </Link>
                            </div>
                        </Reveal>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default Ebook;
